import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger.js";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Animation utility functions
export const animationUtils = {
  // Add hidden class to prevent flashing
  addHiddenClass() {
    const elementsToAnimate = document.querySelectorAll(".example");
    elementsToAnimate.forEach((element) => {
      element.classList.add("gsap-hidden");
    });
  },

  // Remove all hidden classes as fallback
  removeAllHiddenClasses() {
    document.querySelectorAll(".gsap-hidden").forEach((element) => {
      element.classList.remove("gsap-hidden");
    });
  },

  // Clear all ScrollTrigger instances
  clearScrollTriggers() {
    ScrollTrigger.getAll().forEach((st) => st.kill());
  },
};

/*--------------------------------------------------
 * Scroll-triggered animations
 *-------------------------------------------------*/

export const initScrollAnimations = () => {
  // Clear existing ScrollTriggers
  animationUtils.clearScrollTriggers();

  // Batch animations for multiple elements
  const elementsToBatchAnimate = document.querySelectorAll(
    ".info-card, .menu-card, .selection-item, .quick-links .is-style-quick-link",
  );

  if (elementsToBatchAnimate.length > 0) {
    // Apply GSAP state before removing .gsap-hidden so there is no frame at full opacity.
    gsap.set(elementsToBatchAnimate, {
      opacity: 0,
      y: 20,
      immediateRender: true,
    });

    elementsToBatchAnimate.forEach((element) => {
      element.classList.remove("gsap-hidden");
    });

    ScrollTrigger.batch(elementsToBatchAnimate, {
      start: "top 85%",
      end: "bottom 5%",
      onEnter: (batch) =>
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          stagger: { each: 0.15, grid: [1, 4] },
          overwrite: true,
          ease: "power2.inOut",
        }),
    });
  }

  // Individual element animations
  const individualElements = document.querySelectorAll(
    "h1, h2.has-text-align-center",
  );

  individualElements.forEach((element) => {
    gsap.set(element, { opacity: 0, y: 20, immediateRender: true });
    element.classList.remove("gsap-hidden");

    ScrollTrigger.create({
      trigger: element,
      start: "top 85%",
      end: "bottom 5%",
      once: true,
      onEnter: () => {
        gsap.to(element, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          ease: "power2.inOut",
        });
      },
    });
  });

  // Stickers element animations
  const stickersElements = document.querySelectorAll(".sticker");

  stickersElements.forEach((element) => {
    gsap.set(element, { rotation: 0, immediateRender: true });
    element.classList.remove("gsap-hidden");

    const playStickerAnimation = () => {
      gsap.killTweensOf(element);
      gsap.set(element, { rotation: 0 });
      gsap.to(element, {
        rotation: 20,
        duration: 1,
        repeat: 5,
        yoyo: true,
        ease: "sine.inOut",
      });
    };

    const resetStickerAnimation = () => {
      gsap.killTweensOf(element);
      gsap.set(element, { rotation: 0 });
    };

    ScrollTrigger.create({
      trigger: element,
      start: "top 85%",
      end: "bottom 5%",
      once: false,
      onEnter: playStickerAnimation,
      onEnterBack: playStickerAnimation,
      onLeave: resetStickerAnimation,
      onLeaveBack: resetStickerAnimation,
    });
  });

  const individualElementsRight = document.querySelectorAll(
    ".enter-right, .menu--image-right .menu__image-wrapper, .menu--image-left .menu__list",
  );

  individualElementsRight.forEach((element) => {
    element.classList.remove("gsap-hidden");

    gsap.set(element, { opacity: 0, x: 50 });

    ScrollTrigger.create({
      trigger: element,
      start: "top 85%",
      end: "bottom 5%",
      once: true,
      onEnter: () => {
        gsap.to(element, {
          opacity: 1,
          x: 0,
          duration: 0.6,
          ease: "power2.inOut",
        });
      },
    });
  });

  const individualElementsLeft = document.querySelectorAll(
    ".enter-left, .menu--image-left .menu__image-wrapper, .menu--image-right .menu__list, .menu-section__title",
  );

  individualElementsLeft.forEach((element) => {
    element.classList.remove("gsap-hidden");

    gsap.set(element, { opacity: 0, x: -50 });

    ScrollTrigger.create({
      trigger: element,
      start: "top 85%",
      end: "bottom 5%",
      once: true,
      onEnter: () => {
        gsap.to(element, {
          opacity: 1,
          x: 0,
          duration: 0.6,
          ease: "power2.inOut",
        });
      },
    });
  });

  // Mosaic image parallax effect
  const mosaicImages = document.querySelectorAll(
    ".jsx-mosaic__item .jsx-mosaic__image, .team img",
  );

  mosaicImages.forEach((image) => {
    gsap.set(image, { yPercent: -20, scale: 1.3 });

    gsap.to(image, {
      yPercent: 20,
      ease: "none",
      scrollTrigger: {
        trigger: image.closest(".jsx-mosaic__item"),
        start: "top bottom",
        end: "bottom top",
        scrub: true,
      },
    });
  });
};

// Recalculate trigger positions once images/fonts have fully loaded
const handleWindowLoad = () => ScrollTrigger.refresh();

// Keep trigger positions accurate as lazy-loaded images shift page height while scrolling
let bodyResizeObserver;
let refreshDebounce;
const observeBodyResize = () => {
  bodyResizeObserver?.disconnect();
  clearTimeout(refreshDebounce);

  bodyResizeObserver = new ResizeObserver(() => {
    clearTimeout(refreshDebounce);
    refreshDebounce = setTimeout(() => ScrollTrigger.refresh(), 200);
  });
  bodyResizeObserver.observe(document.body);
};

// Main animation initialization
export const initAllAnimations = () => {
  // Add hidden classes first
  animationUtils.addHiddenClass();

  // Small delay for scroll animations to ensure DOM is ready
  setTimeout(() => {
    initScrollAnimations();

    // Failsafe to remove hidden classes
    setTimeout(animationUtils.removeAllHiddenClasses, 1000);
  }, 100);

  window.removeEventListener("load", handleWindowLoad);
  window.addEventListener("load", handleWindowLoad);

  observeBodyResize();
};
